import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useEffect, useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  DataTableSelectFilter,
  DataTableSingleSelectFilter,
} from "./DataTableFilter";
import { useBreakpoint } from "@/lib/hooks/useBreakpoint";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";
import { Search } from "lucide-react";
import { getDefaultHiddenColumns } from "./DataTableUtils";
import { cn } from "@/lib/utils/ui";
import { Skeleton } from "@/components/ui/skeleton";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Button } from "../ui/button";
import { Filter } from "lucide-react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Separator } from "../ui/separator";
import { Switch } from "../ui/switch";
import { ScrollArea, ScrollBar } from "../ui/scroll-area";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuTrigger,
} from "../ui/context-menu";
import type { FilterOptions, ToggleFilterOption } from "./DataTable";
import React from "react";

const GroupedRowContext = React.createContext<{ sectionId: string } | null>(
  null,
);

export function useGroupedRowContext() {
  return React.useContext(GroupedRowContext);
}

type GroupedSection<TData> = {
  id: string;
  label: string;
  indicatorColor?: string | null;
  rows: TData[];
  defaultOpen?: boolean;
};

interface VirtualizedDataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  onRowSelected?: (data: TData) => void;
  onRowClick?: (
    data: TData,
    event?: React.MouseEvent<HTMLTableRowElement>,
  ) => void;
  onRowDoubleClick?: (data: TData) => void;
  onRowContextMenu?: (
    data: TData,
    event?: React.MouseEvent<HTMLElement>,
  ) => void;
  onVisibleRowsChange?: (rows: TData[]) => void;
  onRowDeleted?: (data: TData) => void;
  onRowArchived?: (data: TData) => void;
  onAssignCoaches?: (data: TData) => void;
  isRowSelected?: (data: TData) => boolean;
  getRowDraggable?: (data: TData) => boolean;
  onRowDragStart?: (
    data: TData,
    event: React.DragEvent<HTMLTableRowElement>,
  ) => void;
  onRowDragEnter?: (
    data: TData,
    event: React.DragEvent<HTMLTableRowElement>,
  ) => void;
  onRowDragOver?: (
    data: TData,
    event: React.DragEvent<HTMLTableRowElement>,
  ) => void;
  onRowDragLeave?: (
    data: TData,
    event: React.DragEvent<HTMLTableRowElement>,
  ) => void;
  onRowDrop?: (
    data: TData,
    event: React.DragEvent<HTMLTableRowElement>,
  ) => void;
  onRowDragEnd?: (
    data: TData,
    event: React.DragEvent<HTMLTableRowElement>,
  ) => void;
  getRowClassName?: (data: TData) => string;
  renderRowContextMenu?: (row: TData) => React.ReactNode;
  filterOptions?: FilterOptions[];
  toggleFilter?: ToggleFilterOption;
  inlineFilters?: boolean;
  emptyView?: React.ReactNode;
  showSearchBar?: boolean;
  isLoading?: boolean;
  skeletonRow?: React.ReactNode;
  initialSortColumn?: string;
  initialSortDesc?: boolean;
  searchBarActions?: React.ReactNode;
  groupedSections?: (rows: TData[]) => GroupedSection<TData>[];
  className?: string;
  tableContainerClassName?: string;
  tableWrapperClassName?: string;
  tableHeaderClassName?: string;
  tableClassName?: string;
  tableWrapperProps?: React.HTMLAttributes<HTMLDivElement>;
  stickyHeader?: boolean;
  estimateRowHeight?: number;
  overscan?: number;
  virtualizedHeight?: number | string;
}

export function VirtualizedDataTable<TData, TValue>({
  columns,
  data,
  onRowSelected,
  onRowClick,
  onRowDoubleClick,
  onRowContextMenu,
  onVisibleRowsChange,
  onRowDeleted,
  onRowArchived,
  onAssignCoaches,
  isRowSelected,
  getRowDraggable,
  onRowDragStart,
  onRowDragEnter,
  onRowDragOver,
  onRowDragLeave,
  onRowDrop,
  onRowDragEnd,
  getRowClassName,
  renderRowContextMenu,
  filterOptions,
  toggleFilter,
  inlineFilters = false,
  emptyView,
  showSearchBar,
  isLoading = false,
  skeletonRow,
  initialSortColumn,
  initialSortDesc = false,
  searchBarActions,
  groupedSections,
  className,
  tableContainerClassName,
  tableWrapperClassName,
  tableHeaderClassName,
  tableClassName,
  tableWrapperProps,
  stickyHeader = false,
  estimateRowHeight = 72,
  overscan = 8,
  virtualizedHeight = "70vh",
}: VirtualizedDataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>(
    initialSortColumn ? [{ id: initialSortColumn, desc: initialSortDesc }] : [],
  );
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(() => {
    if (!toggleFilter?.defaultFilterOn) {
      return [];
    }
    return [{ id: toggleFilter.column, value: toggleFilter.filterValue }];
  });
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    getDefaultHiddenColumns(columns),
  );
  const [isScrolledX, setIsScrolledX] = useState(false);
  const [openRowId, setOpenRowId] = useState<string | null>(null);
  const [tableHeaderHeight, setTableHeaderHeight] = useState(0);
  const [tableViewportWidth, setTableViewportWidth] = useState(0);
  const [openGroupedSections, setOpenGroupedSections] = useState<
    Record<string, boolean>
  >({});
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const tableHeaderRef = useRef<HTMLTableSectionElement>(null);
  const {
    className: tableWrapperPropsClassName,
    dir: tableWrapperDir,
    onScroll: tableWrapperPropsOnScroll,
    style: tableWrapperPropsStyle,
    ...tableWrapperRestProps
  } = tableWrapperProps ?? {};

  const { isAboveSm, isBelowSm } = useBreakpoint("sm");
  useEffect(() => {
    const map = getDefaultHiddenColumns(columns);

    if (isBelowSm) {
      const hiddenColumns = columns.filter((col) =>
        col.meta?.isHiddenForSmMediaQuery?.(isBelowSm),
      );
      for (const col of hiddenColumns) {
        map[col.id ?? ""] = false;
      }
    }
    setColumnVisibility(map);
  }, [columns, isAboveSm, isBelowSm]);

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
    },
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    onColumnVisibilityChange: setColumnVisibility,
  });

  const rows = table.getRowModel().rows;
  const visibleRows = useMemo(() => rows.map((row) => row.original), [rows]);
  const groupedSectionData = useMemo(
    () => groupedSections?.(visibleRows) ?? null,
    [groupedSections, visibleRows],
  );
  const rowByOriginal = useMemo(() => {
    const map = new Map<TData, (typeof rows)[number]>();
    rows.forEach((row) => {
      map.set(row.original, row);
    });
    return map;
  }, [rows]);
  const isGrouped = Boolean(
    groupedSectionData && groupedSectionData.length > 0,
  );
  const visibleFlatColumns = table.getVisibleFlatColumns();

  const columnWidths = useMemo(
    () =>
      visibleFlatColumns.map((column) => {
        const className = [
          column.columnDef.meta?.headerClassName,
          column.columnDef.meta?.cellClassName,
        ]
          .filter(Boolean)
          .join(" ");
        const widthMatch = className.match(/(?:^|\s)w-\[(\d+)px\]/);

        return widthMatch ? `${widthMatch[1]}px` : undefined;
      }),
    [visibleFlatColumns],
  );

  useEffect(() => {
    onVisibleRowsChange?.(visibleRows);
  }, [onVisibleRowsChange, visibleRows]);

  useEffect(() => {
    const element = tableHeaderRef.current;
    if (!element) {
      return;
    }

    const updateHeaderHeight = () => {
      setTableHeaderHeight(element.getBoundingClientRect().height);
    };

    updateHeaderHeight();

    const observer = new ResizeObserver(() => {
      updateHeaderHeight();
    });

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const element = tableContainerRef.current;
    if (!element) {
      return;
    }

    const updateViewportWidth = () => {
      setTableViewportWidth(element.clientWidth);
    };

    updateViewportWidth();

    const observer = new ResizeObserver(() => {
      updateViewportWidth();
    });

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!groupedSectionData) {
      setOpenGroupedSections({});
      return;
    }

    setOpenGroupedSections((current) => {
      const next: Record<string, boolean> = {};
      groupedSectionData.forEach((section) => {
        next[section.id] = current[section.id] ?? section.defaultOpen ?? true;
      });
      return next;
    });
  }, [groupedSectionData]);

  const totalFilters = useMemo(() => {
    return columnFilters.reduce((acc, filter) => {
      if (filter.value instanceof Array) {
        return acc + filter.value.length;
      }

      return acc + (filter.value ? 1 : 0);
    }, 0);
  }, [columnFilters]);

  const hasFilters = !!filterOptions?.length || !!toggleFilter;
  const showFiltersInline = inlineFilters && hasFilters;

  const renderToggleFilter = () => {
    if (!toggleFilter) {
      return null;
    }

    return (
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {toggleFilter.label}
        </span>
        <Switch
          checked={
            toggleFilter.invertToggle
              ? !table.getColumn(toggleFilter.column)?.getFilterValue()
              : !!table.getColumn(toggleFilter.column)?.getFilterValue()
          }
          onCheckedChange={(checked) => {
            const shouldFilter = toggleFilter.invertToggle ? !checked : checked;
            table
              .getColumn(toggleFilter.column)
              ?.setFilterValue(
                shouldFilter ? toggleFilter.filterValue : undefined,
              );
          }}
        />
      </div>
    );
  };

  const handleTableScroll = (event: React.UIEvent<HTMLDivElement>) => {
    tableWrapperPropsOnScroll?.(event);
    setIsScrolledX(event.currentTarget.scrollLeft > 0);
  };

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => estimateRowHeight,
    overscan,
  });

  const shouldVirtualizeRows = !isGrouped;
  const virtualRows = rowVirtualizer.getVirtualItems();
  const paddingTop =
    shouldVirtualizeRows && virtualRows.length > 0 ? virtualRows[0].start : 0;
  const paddingBottom =
    shouldVirtualizeRows && virtualRows.length > 0
      ? rowVirtualizer.getTotalSize() - virtualRows[virtualRows.length - 1].end
      : 0;
  const stickySectionOffset = stickyHeader ? tableHeaderHeight || 48 : 0;

  const renderTableRow = (
    row: (typeof rows)[number],
    keyOverride?: string,
    groupedSectionId?: string,
  ) => {
    const rowKey = keyOverride ?? row.id;
    const tableRow = (
      <TableRow
        key={rowKey}
        data-state={
          row.getIsSelected() ||
          openRowId === row.id ||
          isRowSelected?.(row.original)
            ? "selected"
            : undefined
        }
        draggable={Boolean(getRowDraggable?.(row.original))}
        onClick={(event) => {
          if (event.defaultPrevented) return;
          if (event.detail === 2) {
            onRowDoubleClick?.(row.original);
            return;
          }
          const suppressUntil = (
            window as Window & { __suppressRowClickUntil?: number }
          ).__suppressRowClickUntil;
          if (suppressUntil && Date.now() < suppressUntil) return;
          onRowClick?.(row.original, event);
          onRowSelected?.(row.original);
        }}
        onDragStart={(event) => onRowDragStart?.(row.original, event)}
        onDragEnter={(event) => onRowDragEnter?.(row.original, event)}
        onDragOver={(event) => onRowDragOver?.(row.original, event)}
        onDragLeave={(event) => onRowDragLeave?.(row.original, event)}
        onDrop={(event) => onRowDrop?.(row.original, event)}
        onDragEnd={(event) => onRowDragEnd?.(row.original, event)}
        className={cn(
          (onRowSelected || onRowClick || onRowDoubleClick) && "cursor-pointer",
          "group data-[state=selected]:bg-muted data-[state=selected]:shadow-[inset_0_0_0_1px_hsl(var(--border))]",
          getRowClassName?.(row.original),
        )}
      >
        {row.getVisibleCells().map((cell) => {
          const isSticky = cell.column.columnDef.meta?.sticky;
          return (
            <TableCell
              key={cell.id}
              className={cn(
                cell.column.columnDef.meta?.cellClassName,
                isSticky &&
                  "sticky left-0 z-10 bg-background transition-colors group-hover:bg-muted group-data-[state=selected]:bg-muted",
                isSticky &&
                  isScrolledX &&
                  "border-r border-border shadow-[8px_0_12px_-10px_rgba(15,23,42,0.18)]",
              )}
            >
              {flexRender(cell.column.columnDef.cell, {
                ...cell.getContext(),
                onRowSelected,
                onRowDeleted,
                onRowArchived,
                onAssignCoaches,
              })}
            </TableCell>
          );
        })}
      </TableRow>
    );

    const rowContent = renderRowContextMenu ? (
      <ContextMenu
        key={rowKey}
        modal={false}
        onOpenChange={(open) => {
          setOpenRowId((current) => {
            if (open) {
              return row.id;
            }
            return current === row.id ? null : current;
          });
        }}
      >
        <ContextMenuTrigger
          asChild
          onContextMenu={(event) => {
            onRowContextMenu?.(row.original, event);
            setOpenRowId(row.id);
          }}
        >
          {tableRow}
        </ContextMenuTrigger>
        <ContextMenuContent>
          {renderRowContextMenu(row.original)}
        </ContextMenuContent>
      </ContextMenu>
    ) : (
      tableRow
    );

    if (!groupedSectionId) {
      return rowContent;
    }

    return (
      <GroupedRowContext.Provider
        key={rowKey}
        value={{ sectionId: groupedSectionId }}
      >
        {rowContent}
      </GroupedRowContext.Provider>
    );
  };

  const renderGroupedSectionHeader = (
    section: GroupedSection<TData>,
    rowCount: number,
  ) => {
    const isOpen = openGroupedSections[section.id] ?? true;

    return (
      <div
        className="sticky z-30 bg-background py-1"
        style={{
          top: `${stickySectionOffset}px`,
        }}
      >
        <button
          type="button"
          className="sticky left-0 flex items-center gap-3 overflow-hidden rounded-xl bg-secondary/70 px-4 py-3 text-left backdrop-blur supports-[backdrop-filter]:bg-secondary/60"
          style={{
            width:
              tableViewportWidth > 0 ? `${tableViewportWidth}px` : undefined,
          }}
          onClick={() =>
            setOpenGroupedSections((current) => ({
              ...current,
              [section.id]: !isOpen,
            }))
          }
        >
          {isOpen ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          )}
          <span
            className="h-2.5 w-2.5 shrink-0 rounded-full border border-black/5"
            style={{
              backgroundColor:
                section.indicatorColor ?? "hsl(var(--muted-foreground))",
            }}
            aria-hidden="true"
          />
          <span className="truncate text-sm font-semibold">
            {section.label}
          </span>
          <span className="shrink-0 text-sm text-muted-foreground">
            {rowCount}
          </span>
        </button>
      </div>
    );
  };

  const renderColumnGroup = () => (
    <colgroup>
      {visibleFlatColumns.map((column, index) => (
        <col key={column.id} style={{ width: columnWidths[index] }} />
      ))}
    </colgroup>
  );

  const renderTableHeader = ({ sticky = true }: { sticky?: boolean } = {}) => (
    <TableHeader
      ref={tableHeaderRef}
      className={cn(
        sticky &&
          stickyHeader &&
          "sticky top-0 z-30 bg-background shadow-[inset_0_-1px_0_hsl(var(--border))]",
        tableHeaderClassName,
      )}
    >
      {table.getHeaderGroups().map((headerGroup) => (
        <TableRow key={headerGroup.id} className="hover:bg-transparent">
          {headerGroup.headers.map((header) => {
            const isSticky = header.column.columnDef.meta?.sticky;
            return (
              <TableHead
                key={header.id}
                className={cn(
                  "transition-colors",
                  header.column.columnDef.meta?.headerClassName,
                  isSticky && "sticky left-0 top-0 z-40 bg-background",
                  !isSticky && stickyHeader && "top-0",
                  isSticky &&
                    isScrolledX &&
                    "border-r border-border shadow-[8px_0_12px_-10px_rgba(15,23,42,0.25)]",
                )}
              >
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
              </TableHead>
            );
          })}
        </TableRow>
      ))}
    </TableHeader>
  );

  return (
    <div className={cn("flex flex-col", className)}>
      <div className="pb-2 flex flex-col md:flex-row md:flex-wrap md:items-center gap-2 md:gap-4">
        {showSearchBar && (
          <div className="flex items-center gap-2">
            <InputGroup className="h-8 flex-1 md:flex-none md:w-[170px] lg:w-[250px]">
              <InputGroupAddon>
                <Search className="size-3 text-muted-foreground" />
              </InputGroupAddon>
              <InputGroupInput
                placeholder="Search..."
                value={
                  (table.getColumn("name")?.getFilterValue() as string) ?? ""
                }
                onChange={(event) => {
                  const column = table.getColumn("name");
                  column?.setFilterValue(event.target.value);
                  if (column?.getIsSorted() !== "asc") {
                    column?.toggleSorting();
                  }
                }}
                className="h-8 px-2 text-sm"
              />
            </InputGroup>
            {!showFiltersInline && hasFilters && (
              <Popover modal={true}>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="relative h-8 w-8 shrink-0 p-0"
                    aria-label="Open filters"
                  >
                    <Filter className="size-4" />
                    {totalFilters > 0 && (
                      <span className="absolute right-1.5 top-1.5 size-2 rounded-full bg-blue-500" />
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent>
                  <div className="flex flex-row justify-between items-center">
                    <h1 className="font-semibold">Filters</h1>
                    <Button
                      variant="ghost"
                      onClick={() => table.resetColumnFilters()}
                    >
                      Clear filters
                    </Button>
                  </div>
                  <Separator className="my-2" />
                  <div className="flex flex-col gap-3">
                    {toggleFilter && (
                      <div className="flex flex-row justify-between item-center px-2">
                        {toggleFilter.label}
                        <Switch
                          checked={
                            toggleFilter.invertToggle
                              ? !table
                                  .getColumn(toggleFilter.column)
                                  ?.getFilterValue()
                              : !!table
                                  .getColumn(toggleFilter.column)
                                  ?.getFilterValue()
                          }
                          onCheckedChange={(checked) => {
                            const shouldFilter = toggleFilter.invertToggle
                              ? !checked
                              : checked;
                            table
                              .getColumn(toggleFilter.column)
                              ?.setFilterValue(
                                shouldFilter
                                  ? toggleFilter.filterValue
                                  : undefined,
                              );
                          }}
                        />
                      </div>
                    )}
                    {filterOptions?.map((filterOption) =>
                      filterOption.singleSelect ? (
                        <DataTableSingleSelectFilter
                          key={filterOption.column}
                          options={filterOption.options ?? []}
                          column={table.getColumn(filterOption.column)}
                          title={filterOption.title}
                          includeAllOption={filterOption.includeAllOption}
                        />
                      ) : (
                        <DataTableSelectFilter
                          key={filterOption.column}
                          options={filterOption.options ?? []}
                          column={table.getColumn(filterOption.column)}
                          title={filterOption.title}
                        />
                      ),
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>
        )}

        {(searchBarActions ||
          showFiltersInline ||
          (hasFilters && !showSearchBar)) && (
          <div className="flex flex-wrap items-center gap-3 md:ml-auto">
            {searchBarActions}
            {showFiltersInline ? (
              <>
                {filterOptions?.map((filterOption) =>
                  filterOption.singleSelect ? (
                    <DataTableSingleSelectFilter
                      key={filterOption.column}
                      options={filterOption.options ?? []}
                      column={table.getColumn(filterOption.column)}
                      title={filterOption.title}
                      includeAllOption={filterOption.includeAllOption}
                    />
                  ) : (
                    <DataTableSelectFilter
                      key={filterOption.column}
                      options={filterOption.options ?? []}
                      column={table.getColumn(filterOption.column)}
                      title={filterOption.title}
                    />
                  ),
                )}
                {renderToggleFilter()}
              </>
            ) : null}
          </div>
        )}
      </div>

      <div
        className={cn(
          "rounded-md overflow-hidden",
          isLoading || rows.length > 0 ? tableContainerClassName : undefined,
        )}
      >
        <ScrollArea
          className={cn(
            "w-full",
            tableWrapperClassName,
            tableWrapperPropsClassName,
          )}
          dir={
            tableWrapperDir === "ltr" || tableWrapperDir === "rtl"
              ? tableWrapperDir
              : undefined
          }
          scrollbarClassName={
            stickyHeader ? "z-50 mt-12 h-[calc(100%-3rem)]" : "z-50"
          }
          style={{
            ...tableWrapperPropsStyle,
            height: virtualizedHeight,
          }}
          viewportRef={tableContainerRef}
          viewportProps={{
            onScroll: handleTableScroll,
          }}
          viewportClassName="h-full w-full"
          {...tableWrapperRestProps}
        >
          {isGrouped && !isLoading ? (
            <>
              <div
                className={cn(
                  stickyHeader &&
                    "sticky top-0 z-40 bg-background shadow-[inset_0_-1px_0_hsl(var(--border))]",
                )}
              >
                <table
                  className={cn(
                    "w-full caption-bottom text-sm",
                    tableClassName,
                  )}
                >
                  {renderColumnGroup()}
                  {renderTableHeader({ sticky: false })}
                </table>
              </div>
              <div className={cn("w-full text-sm", tableClassName)}>
                {groupedSectionData?.map((section) => {
                  const sectionRows = section.rows
                    .map((row) => rowByOriginal.get(row))
                    .filter((row): row is (typeof rows)[number] => !!row);
                  const isOpen = openGroupedSections[section.id] ?? true;

                  return (
                    <section key={section.id} className="w-full">
                      {renderGroupedSectionHeader(section, sectionRows.length)}
                      {isOpen && (
                        <table
                          className={cn(
                            "w-full caption-bottom text-sm",
                            tableClassName,
                          )}
                        >
                          {renderColumnGroup()}
                          <TableBody>
                            {sectionRows.map((row, index) =>
                              renderTableRow(
                                row,
                                `${section.id}-${row.id}-${index}`,
                                section.id,
                              ),
                            )}
                          </TableBody>
                        </table>
                      )}
                    </section>
                  );
                })}
              </div>
            </>
          ) : (
            <table
              className={cn("w-full caption-bottom text-sm", tableClassName)}
            >
              {renderColumnGroup()}
              {renderTableHeader()}
              <TableBody>
                {isLoading &&
                  Array.from({ length: 8 }).map((_, i) =>
                    skeletonRow ? (
                      <TableRow
                        key={`skeleton-${i}`}
                        className="hover:bg-transparent"
                      >
                        <TableCell
                          colSpan={visibleFlatColumns.length}
                          className="p-0"
                        >
                          {skeletonRow}
                        </TableCell>
                      </TableRow>
                    ) : (
                      <TableRow
                        key={`skeleton-${i}`}
                        className="hover:bg-transparent"
                      >
                        {visibleFlatColumns.map((col) => (
                          <TableCell key={col.id}>
                            <Skeleton className="h-4 w-full" />
                          </TableCell>
                        ))}
                      </TableRow>
                    ),
                  )}
                {!isLoading && rows.length > 0 && paddingTop > 0 && (
                  <TableRow className="hover:bg-transparent">
                    <TableCell
                      colSpan={visibleFlatColumns.length}
                      className="p-0"
                      style={{ height: `${paddingTop}px` }}
                    />
                  </TableRow>
                )}
                {!isLoading &&
                  virtualRows.map((virtualRow) => {
                    const row = rows[virtualRow.index];

                    if (!row) {
                      return null;
                    }

                    return renderTableRow(row, row.id);
                  })}
                {!isLoading && rows.length > 0 && paddingBottom > 0 && (
                  <TableRow className="hover:bg-transparent">
                    <TableCell
                      colSpan={visibleFlatColumns.length}
                      className="p-0"
                      style={{ height: `${paddingBottom}px` }}
                    />
                  </TableRow>
                )}
              </TableBody>
            </table>
          )}
          <ScrollBar orientation="horizontal" className="z-50" />
        </ScrollArea>
      </div>
      {!isLoading && rows.length === 0 && (
        <div className="flex flex-1 min-h-[300px] items-start justify-center pt-12">
          {emptyView ?? "No results."}
        </div>
      )}
    </div>
  );
}
